import { RubyGemMark } from '../../components/RubyGemMark';
import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, CheckCircle2, Truck } from 'lucide-react';
import {
  hardwareApi,
  EXCEPTION_FLOW,
  REASON_REQUIRED,
  UNIT_STATUS_LABEL,
  unitTransitionBlock,
  type DeviceModelRow,
  type DeviceUnitRow,
} from '../../services/hardwareApi';
import type { UnitStatus } from '@platform/shared-types';
import { toastService } from '../../services/toastService';

/**
 * EACH DEVICE, TRACKED — AND NOBODY PUSHES IT ALONG BY HAND.
 *
 * The step bar used to be eight buttons. Clicking "Shipped" on a device that
 * was never boxed produced exactly the status mismatch this screen exists to
 * prevent: the order said one thing, the warehouse another. Steps now move from
 * the event that makes them true, and this component only SHOWS where each
 * device is and what moves it next:
 *
 *   Awaiting     → unit on the order, nothing picked from stock yet
 *   Processing   → a real unit allocated from stock (state `allocated`)
 *   Provisioning → setup started on the device
 *   Configured   → every required setup step ticked, terminal id set
 *   Packing      → put in a box
 *   Shipped      → order marked shipped, with each box's tracking
 *   Delivered    → confirmed in the Delivered stage
 *   Deployed     → confirmed live in the Live stage
 *
 * The one thing an operator still sets is a PROBLEM — on hold, backordered,
 * returned, RMA, retired, back to stock. Those are facts only a person knows,
 * and the order header lists every device that has one.
 */

/** The steps as drawn. `allocated` has no step of its own; it IS Processing. */
const STEPS: UnitStatus[] = [
  'awaiting', 'processing', 'provisioning', 'configured', 'packing', 'shipped', 'delivered', 'deployed',
];

const NEXT: Partial<Record<UnitStatus, string>> = {
  awaiting: 'Moves to Processing when a device is allocated from stock in the Allocate stage.',
  processing: 'Moves to Provisioning when setup is started in the Provisioning stage.',
  provisioning: 'Moves to Configured when every required setup step is ticked and a terminal id is set.',
  configured: 'Moves to Packing when this device is put in a box.',
  packing: 'Moves to Shipped when the order is marked shipped with tracking.',
  shipped: 'Moves to Delivered when delivery is confirmed in the Delivered stage.',
  delivered: 'Moves to Deployed when it is confirmed live in the Live stage.',
  deployed: 'Live at the store.',
};

const EXCEPTIONS = new Set<UnitStatus>(EXCEPTION_FLOW);

const PROBLEM_HINT: Partial<Record<UnitStatus, string>> = {
  on_hold: 'Paused — the merchant sees the reason',
  backordered: 'Waiting on stock — the merchant sees the reason',
  returned: 'Came back from the merchant',
  rma: 'Sent back to the vendor',
  retired: 'Out of service for good',
  // Arrived here on its own the moment `lost` joined EXCEPTION_FLOW — the menu
  // reads that list rather than repeating it. Only the hint had to be written.
  lost: 'Never arrived — a carrier claim, not a return',
};

/**
 * Where a device picks up again when its problem is cleared.
 *
 * A unit row does not remember the state it held before the problem, so this
 * reads the EVIDENCE the device carries instead — the same facts that moved it
 * forward in the first place. A resumed device therefore lands exactly where
 * its real progress says, never where somebody last clicked.
 */
export function resumeStateOf(unit: DeviceUnitRow): UnitStatus {
  const u = unit as any;
  if (u.shipment?.shippedAt) return 'shipped';
  if (u.boxNo) return 'packing';
  const steps = u.provisioning?.steps;
  if (Array.isArray(steps)) {
    return steps.filter((s: any) => s.required).every((s: any) => s.done) ? 'configured' : 'provisioning';
  }
  return 'allocated';
}

/** Which drawn step a state sits on. Problems sit where the device's evidence is. */
function stepIndexOf(unit: DeviceUnitRow, state: UnitStatus): number {
  const s = EXCEPTIONS.has(state) || state === 'in_stock' ? resumeStateOf(unit) : state;
  if (s === 'allocated') return STEPS.indexOf('processing');
  const i = STEPS.indexOf(s);
  return i < 0 ? 0 : i;
}

const shortDate = (ts?: number) =>
  ts ? new Date(ts).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : '';

export interface TrackerDraft {
  state: UnitStatus;
  serialNumber: string;
  holdReason: string;
}

/* ── the problem menu — a custom dropdown, never a native select ───────── */

/**
 * EXPORTED so the bench can offer the same menu.
 *
 * The Pending configuration screen works one device at a time and needs the
 * identical vocabulary — on hold, backordered, returned, RMA, retired, lost,
 * back to stock, clear. A second menu would have drifted from this one the
 * first time a state was added, which is exactly what `lost` just proved:
 * it appeared here automatically because this reads EXCEPTION_FLOW rather
 * than listing states by hand.
 *
 * It stays a PURE picker — it reports what was chosen and writes nothing. The
 * order screen stages picks into a draft and saves them together; the bench
 * applies one immediately. Same menu, two policies.
 */
export const ProblemMenu: React.FC<{
  unit: DeviceUnitRow;
  current: UnitStatus;
  orderStatus?: string;
  onPick: (state: UnitStatus) => void;
}> = ({ unit, current, orderStatus, onPick }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc); };
  }, [open]);

  const hasProblem = EXCEPTIONS.has(current) || current === 'in_stock';
  const stockBlock = unitTransitionBlock(unit, 'in_stock', orderStatus as any);
  const pick = (s: UnitStatus) => { setOpen(false); onPick(s); };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="menu"
        className={`inline-flex min-h-[44px] items-center gap-1.5 rounded-xl border px-3.5 text-[12.5px] font-semibold transition ${
          hasProblem
            ? 'border-red-200 bg-white text-red-700 hover:bg-red-50'
            : 'border-stone-200 bg-white text-stone-600 hover:border-stone-300 hover:text-stone-900'}`}
      >
        {hasProblem ? UNIT_STATUS_LABEL[current] : 'Report a problem'}
        <ChevronDown size={14} />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-[calc(100%+6px)] z-30 w-64 rounded-xl border border-stone-200 bg-white p-1.5 shadow-xl"
        >
          {EXCEPTION_FLOW.map((s) => (
            <button
              key={s}
              role="menuitem"
              type="button"
              onClick={() => pick(s)}
              className={`grid min-h-[44px] w-full rounded-lg px-2.5 py-1.5 text-left hover:bg-stone-50 ${current === s ? 'bg-red-50' : ''}`}
            >
              <span className="text-[13px] font-medium text-stone-900">{UNIT_STATUS_LABEL[s]}</span>
              {PROBLEM_HINT[s] && <span className="text-[11.5px] text-stone-500">{PROBLEM_HINT[s]}</span>}
            </button>
          ))}
          <div className="my-1 border-t border-stone-200" />
          <button
            role="menuitem"
            type="button"
            disabled={!!stockBlock}
            title={stockBlock || undefined}
            onClick={() => pick('in_stock')}
            className="grid min-h-[44px] w-full rounded-lg px-2.5 py-1.5 text-left hover:bg-stone-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <span className="text-[13px] font-medium text-stone-900">Back to stock</span>
            <span className="text-[11.5px] text-stone-500">Unallocate — only before it ships</span>
          </button>
          {hasProblem && (
            <button
              role="menuitem"
              type="button"
              onClick={() => pick(resumeStateOf(unit))}
              className="grid min-h-[44px] w-full rounded-lg px-2.5 py-1.5 text-left hover:bg-emerald-50"
            >
              <span className="text-[13px] font-medium text-emerald-700">Clear problem</span>
              <span className="text-[11.5px] text-stone-500">
                Resume at {UNIT_STATUS_LABEL[resumeStateOf(unit)]}
              </span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * WHERE THIS DEVICE IS, DRAWN — read-only, nothing here is a button.
 *
 * Lifted out of the tracker so the bench can draw the identical bar. Two
 * copies of a progress rail is how one of them quietly stops matching the
 * states it is drawing, and this one is the thing people read to decide
 * whether a device is where they think it is.
 *
 * `compact` drops the per-step dates and tightens the rail for a row inside a
 * list, where the bar is context rather than the subject.
 */
export const DeviceSteps: React.FC<{
  unit: DeviceUnitRow;
  /** The state to draw — a draft on the order screen, the live one on the bench. */
  state: UnitStatus;
  compact?: boolean;
}> = ({ unit, state, compact }) => {
  const problem = EXCEPTIONS.has(state) || state === 'in_stock';
  const at = stepIndexOf(unit, state);

  return (
    <div className="overflow-x-auto">
      <ol className={`grid grid-cols-8 ${compact ? 'min-w-[520px]' : 'min-w-[640px]'}`}>
        {STEPS.map((s, i) => {
          const done = i < at;
          const now = i === at;
          return (
            <li key={s} className="relative grid justify-items-center gap-1 text-center">
              {i > 0 && (
                <span
                  aria-hidden
                  className={`absolute left-[-50%] right-1/2 ${compact ? 'top-[8px]' : 'top-[10px]'} h-[2px] ${done || now ? 'bg-rose-600' : 'bg-stone-200'}`}
                />
              )}
              <span
                className={`relative z-[1] grid place-items-center rounded-full border-2 ${
                  compact ? 'h-[18px] w-[18px]' : 'h-[22px] w-[22px]'} ${
                  done ? 'border-rose-600 bg-rose-600 text-white'
                  : now ? (problem
                    ? 'border-red-600 bg-white ring-4 ring-red-100'
                    : 'border-rose-600 bg-white ring-4 ring-rose-50')
                  : 'border-stone-200 bg-white'}`}
              >
                {done && <Check size={compact ? 10 : 12} strokeWidth={3} />}
                {now && <span className={`rounded-full ${compact ? 'h-1.5 w-1.5' : 'h-2 w-2'} ${problem ? 'bg-red-600' : 'bg-rose-600'}`} />}
              </span>
              <span className={`${compact ? 'text-[10.5px]' : 'text-[12px]'} ${now ? 'font-semibold text-stone-900' : done ? 'text-stone-600' : 'text-stone-400'}`}>
                {UNIT_STATUS_LABEL[s]}
              </span>
              {!compact && (
                <span className="min-h-[16px] text-[11px] tabular-nums text-stone-400">
                  {s === 'shipped' && unit.shipment?.shippedAt ? shortDate(unit.shipment.shippedAt) : ''}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
};

/** What moves this device on from where it is now. */
export const nextStepHint = (unit: DeviceUnitRow, state: UnitStatus): { next?: string; hint?: string } => {
  if (EXCEPTIONS.has(state) || state === 'in_stock') return {};
  const at = stepIndexOf(unit, state);
  return {
    next: at < STEPS.length - 1 ? UNIT_STATUS_LABEL[STEPS[at + 1]] : undefined,
    hint: NEXT[STEPS[at]],
  };
};

/** Where progress is paused, named — for a problem banner. */
export const pausedAt = (unit: DeviceUnitRow, state: UnitStatus): string =>
  UNIT_STATUS_LABEL[STEPS[stepIndexOf(unit, state)]];

/* ── the tracker ───────────────────────────────────────────────────────── */

const DeviceTracker: React.FC<{
  units: DeviceUnitRow[];
  models: DeviceModelRow[];
  orderStatus?: string;
  drafts: Record<string, TrackerDraft>;
  draftOf: (u: DeviceUnitRow) => TrackerDraft;
  onDraft: (unitId: string, change: Partial<TrackerDraft>) => void;
  renderThumb: (unit: DeviceUnitRow, model?: DeviceModelRow) => React.ReactNode;
}> = ({ units, models, orderStatus, drafts, draftOf, onDraft, renderThumb }) => {
  const modelOf = (id: string) => models.find((m) => m.modelId === id);

  return (
    <section className="mb-4 overflow-visible rounded-2xl border border-stone-200 bg-white">
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-stone-200 px-5 py-3.5">
        <h3 className="text-[15px] font-semibold text-stone-900">Devices</h3>
        <span className="text-[12px] text-stone-400">
          Each device tracked separately · the order shows the least-advanced one
        </span>
      </div>

      {units.length === 0 && (
        <p className="px-5 py-8 text-center text-[13px] text-stone-500">
          No devices allocated yet — allocate them in the Allocate stage above.
        </p>
      )}

      {units.map((unit) => {
        const d = drafts[unit.unitId] || draftOf(unit);
        const model = modelOf(unit.modelId);
        const problem = EXCEPTIONS.has(d.state) || d.state === 'in_stock';
        const moved = d.state !== unit.state;
        const at = stepIndexOf(unit, d.state);
        const frozen = unitTransitionBlock(unit, unit.state, orderStatus as any);

        return (
          <div
            key={unit.unitId}
            className={`relative grid gap-3.5 border-b border-stone-200 px-5 py-4 last:border-b-0 ${
              problem ? 'bg-gradient-to-r from-red-50 to-transparent' : ''}`}
          >
            {problem && <span aria-hidden className="absolute inset-y-0 left-0 w-[3px] bg-red-600" />}

            <div className="flex flex-wrap items-center gap-3">
              {renderThumb(unit, model)}
              <div className="min-w-[200px] flex-1">
                <p className="truncate text-[14px] font-semibold text-stone-900">{model?.modelName || unit.modelId}</p>
                <p className="truncate font-mono text-[11px] text-stone-400">{unit.unitId}</p>
              </div>
              {moved && (
                <span className="rounded-md bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
                  Unsaved → {UNIT_STATUS_LABEL[d.state]}
                </span>
              )}
              <input
                value={d.serialNumber}
                onChange={(e) => onDraft(unit.unitId, { serialNumber: e.target.value })}
                disabled={!!frozen}
                title={frozen || 'Serial number for this device'}
                placeholder="Serial number"
                aria-label={`Serial number for ${model?.modelName || unit.modelId}`}
                className="min-h-[44px] w-48 rounded-lg border border-stone-200 bg-stone-50 px-2.5 font-mono text-[12.5px] text-stone-700 placeholder-stone-400 focus:border-rose-500 focus:outline-none focus:ring-2 focus:ring-rose-500/10 disabled:cursor-not-allowed disabled:opacity-60"
              />
              <ProblemMenu
                unit={unit}
                current={d.state}
                orderStatus={orderStatus}
                onPick={(s) => onDraft(unit.unitId, { state: s })}
              />
            </div>

            {/* Read-only. Nothing here is a button. */}
            <div className="overflow-x-auto">
              <ol className="grid min-w-[640px] grid-cols-8">
                {STEPS.map((s, i) => {
                  const done = i < at;
                  const now = i === at;
                  return (
                    <li key={s} className="relative grid justify-items-center gap-1.5 text-center">
                      {i > 0 && (
                        <span
                          aria-hidden
                          className={`absolute left-[-50%] right-1/2 top-[10px] h-[2px] ${done || now ? 'bg-rose-600' : 'bg-stone-200'}`}
                        />
                      )}
                      <span
                        className={`relative z-[1] grid h-[22px] w-[22px] place-items-center rounded-full border-2 ${
                          done ? 'border-rose-600 bg-rose-600 text-white'
                          : now ? (problem
                            ? 'border-red-600 bg-white ring-4 ring-red-100'
                            : 'border-rose-600 bg-white ring-4 ring-rose-50')
                          : 'border-stone-200 bg-white'}`}
                      >
                        {done && <Check size={12} strokeWidth={3} />}
                        {now && <span className={`h-2 w-2 rounded-full ${problem ? 'bg-red-600' : 'bg-rose-600'}`} />}
                      </span>
                      <span className={`text-[12px] ${now ? 'font-semibold text-stone-900' : done ? 'text-stone-600' : 'text-stone-400'}`}>
                        {UNIT_STATUS_LABEL[s]}
                      </span>
                      <span className="min-h-[16px] text-[11px] tabular-nums text-stone-400">
                        {s === 'shipped' && unit.shipment?.shippedAt ? shortDate(unit.shipment.shippedAt) : ''}
                      </span>
                    </li>
                  );
                })}
              </ol>
            </div>

            {!problem && NEXT[STEPS[at]] && (
              <p className="text-[12.5px] text-stone-600">
                {at < STEPS.length - 1 && <>Next: <b className="text-stone-900">{UNIT_STATUS_LABEL[STEPS[at + 1]]}</b> — </>}
                {NEXT[STEPS[at]]}
              </p>
            )}

            {problem && (
              <div className="flex flex-wrap items-center gap-3 rounded-xl border border-red-200 bg-white px-3.5 py-2.5">
                <p className="min-w-[200px] flex-1 text-[13px] text-stone-600">
                  <b className="text-red-700">{UNIT_STATUS_LABEL[d.state]}</b>
                  {d.state === 'in_stock'
                    ? ' — this device comes off the order when you save.'
                    : ` — progress is paused at ${UNIT_STATUS_LABEL[STEPS[at]]}.`}
                </p>
                {REASON_REQUIRED.has(d.state) && (
                  <input
                    value={d.holdReason}
                    onChange={(e) => onDraft(unit.unitId, { holdReason: e.target.value })}
                    placeholder="Reason — the merchant sees this word for word"
                    aria-label={`Reason this device is ${UNIT_STATUS_LABEL[d.state]}`}
                    className="min-h-[44px] w-full rounded-lg border border-red-200 bg-white px-2.5 text-[13px] text-stone-800 placeholder-stone-400 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/10 sm:w-auto sm:flex-[2]"
                  />
                )}
              </div>
            )}

            {unit.shipment?.trackingNumber && (
              <p className="text-[12px] text-stone-500">
                <Truck size={12} className="mr-1 inline" />
                Shipped {shortDate(unit.shipment.shippedAt)} · {unit.shipment.carrier}{' '}
                <span className="font-mono">{unit.shipment.trackingNumber}</span>
              </p>
            )}
          </div>
        );
      })}
    </section>
  );
};

export default DeviceTracker;

/* ── Delivered / Live stages — the confirmation that moves those steps ── */

/**
 * CONFIRM, IN ORDER. Delivered and Deployed have no machine event behind them
 * — there is no carrier feed and no terminal-activation record — so a person
 * confirms them. But only FORWARD and only from the step before: a device that
 * has not shipped is never offered "delivered". That keeps the confirmation
 * from becoming the manual step bar again under another name.
 */
export const ConfirmUnitsPanel: React.FC<{
  units: DeviceUnitRow[];
  models: DeviceModelRow[];
  from: UnitStatus;
  to: UnitStatus;
  action: string;
  explain: string;
  onUnitChanged: (u: DeviceUnitRow) => void;
  onDone?: () => void;
}> = ({ units, models, from, to, action, explain, onUnitChanged, onDone }) => {
  const [busy, setBusy] = useState(false);
  const eligible = units.filter((u) => u.state === from);
  const reached = units.filter((u) => stepIndexOf(u, u.state) >= STEPS.indexOf(to) && !EXCEPTIONS.has(u.state));
  const name = (u: DeviceUnitRow) => models.find((m) => m.modelId === u.modelId)?.modelName || u.modelId;

  const confirm = async () => {
    setBusy(true);
    const failures: string[] = [];
    for (const u of eligible) {
      try {
        const res: any = await hardwareApi.setUnitStatus(u.unitId, { state: to });
        if (res?.unit) onUnitChanged(res.unit);
      } catch (e: any) {
        failures.push(`${name(u)}: ${e?.message || 'refused'}`);
      }
    }
    setBusy(false);
    if (failures.length) failures.forEach((f) => toastService.error(f));
    else toastService.success(`${eligible.length} device${eligible.length === 1 ? '' : 's'} marked ${UNIT_STATUS_LABEL[to]}.`);
    onDone?.();
  };

  return (
    <div className="space-y-3 text-[12.5px] text-stone-600">
      <p className="leading-relaxed">{explain}</p>
      {units.length > 0 && (
        <ul className="space-y-1">
          {units.map((u) => {
            const ok = reached.includes(u);
            return (
              <li key={u.unitId} className="flex items-center gap-2">
                <span className={`grid h-4 w-4 place-items-center rounded-full ${ok ? 'bg-emerald-500 text-white' : 'border border-stone-300 bg-white'}`}>
                  {ok && <Check size={10} strokeWidth={3} />}
                </span>
                <span className="flex-1 truncate text-stone-800">{name(u)}</span>
                <span className="text-[11.5px] text-stone-400">{UNIT_STATUS_LABEL[u.state]}</span>
              </li>
            );
          })}
        </ul>
      )}
      {eligible.length > 0 ? (
        <button
          type="button"
          onClick={confirm}
          disabled={busy}
          className="inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-rose-600 px-4 text-[13px] font-semibold text-white hover:bg-rose-700 disabled:opacity-50"
        >
          {busy ? <RubyGemMark size={14} drawing color="currentColor" /> : <CheckCircle2 size={14} />}
          {action} ({eligible.length})
        </button>
      ) : (
        <p className="text-[12px] text-stone-400">
          {reached.length === units.length && units.length > 0
            ? `Every device is ${UNIT_STATUS_LABEL[to]}.`
            : `Nothing is at ${UNIT_STATUS_LABEL[from]} yet.`}
        </p>
      )}
    </div>
  );
};
